import { memo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';

import { ContactProfilePanel } from './Panels/ContactProfilePanel';
import { ContextVariablesPanel } from './Panels/ContextVariablesPanel';
import { OverviewPanel } from './Panels/OverviewPanel';
import { ResizeHandle } from '../MessagesList/ResizeHandle';
import { useResizablePanel } from '../MessagesList/useResizablePanel';

type Tab = 'overview' | 'context' | 'contact';

const MIN_WIDTH = 320;
const MAX_WIDTH = 512;
const DEFAULT_WIDTH = 320;
const STORAGE_KEY = 'humanChat.rightSidebar.width';

export const RightSidebar = memo(() => {
  // Custom hooks
  const { t } = useTranslation();

  // Local state
  const [activeTab, setActiveTab] = useState<Tab>('overview');

  const {
    width: panelWidth,
    panelRef,
    isActive,
    separatorProps,
  } = useResizablePanel({
    minWidth: MIN_WIDTH,
    maxWidth: MAX_WIDTH,
    initialWidth: DEFAULT_WIDTH,
    resizeFrom: 'left',
    storageKey: STORAGE_KEY,
  });

  // Local variables
  const showOverviewPanel = activeTab === 'overview';
  const showContextVariablesPanel = activeTab === 'context';
  const showContactProfilePanel = activeTab === 'contact';

  return (
    <div
      ref={panelRef}
      style={{ width: panelWidth }}
      className="relative flex h-[calc(100vh-var(--newheader-height))] shrink-0 flex-col bg-bg-muted-light"
    >
      <div className="flex h-14 shrink-0 items-center border-b border-stroke bg-background p-2">
        <Tabs
          value={activeTab}
          onValueChange={(v) => setActiveTab(v as Tab)}
          className="w-full"
          data-testid="tabbed-header"
        >
          <TabsList className="w-full">
            <TabsTrigger value="overview" className="flex-1" data-testid="overview-tab">
              {t('analytics.overview')}
            </TabsTrigger>
            <TabsTrigger value="contact" className="flex-1" data-testid="contact-profile-tab">
              {t('common.contact')}
            </TabsTrigger>
            <TabsTrigger value="context" className="flex-1" data-testid="context-variables-tab">
              {t('common.context')}
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      <div className="flex-1 overflow-x-hidden overflow-y-auto p-2">
        {showOverviewPanel && <OverviewPanel />}
        {showContextVariablesPanel && <ContextVariablesPanel />}
        {showContactProfilePanel && <ContactProfilePanel />}
      </div>
      <ResizeHandle
        separatorProps={separatorProps}
        isActive={isActive}
        label={t('conversation.resize_panel')}
        side="left"
      />
    </div>
  );
});

RightSidebar.displayName = 'RightSidebar';
